import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { formatDate, formatApiError } from '../../lib/utils';
import { Upload, Trash2, ArrowLeft, FileText, Package, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminListingTickets() {
    const { listingId } = useParams();
    const [listing, setListing] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [summary, setSummary] = useState({ total: 0, available: 0, assigned: 0 });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadOptionId, setUploadOptionId] = useState('');
    const [uploadType, setUploadType] = useState('adult');
    const [uploadDate, setUploadDate] = useState('');
    const [uploadTimeSlot, setUploadTimeSlot] = useState('');
    const [filterOption, setFilterOption] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterDate, setFilterDate] = useState('');

    useEffect(() => { fetchListing(); fetchTickets(); }, [listingId]);

    const fetchListing = async () => {
        try { const res = await axios.get(`${API}/api/admin/listings/${listingId}`, { withCredentials: true }); setListing(res.data); const opts = res.data.options?.filter(o => o.has_tickets) || []; if (opts.length > 0 && !uploadOptionId) setUploadOptionId(opts[0].id); }
        catch { toast.error('Failed to load listing'); }
    };

    const fetchTickets = async () => {
        try {
            const p = new URLSearchParams();
            if (filterOption) p.append('option_id', filterOption);
            if (filterType) p.append('ticket_type', filterType);
            if (filterDate) p.append('date', filterDate);
            const res = await axios.get(`${API}/api/admin/listings/${listingId}/tickets?${p}`, { withCredentials: true });
            setTickets(res.data.tickets); setSummary(res.data.summary);
        } catch { toast.error('Failed to load tickets'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchTickets(); }, [filterOption, filterType, filterDate]);

    const handleUpload = async (e) => {
        const files = e.target.files; if (!files?.length) return;
        if (!uploadDate) { toast.error('Please select a date'); return; }
        if (!uploadOptionId) { toast.error('Please select an option'); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            Array.from(files).forEach(f => fd.append('files', f));
            const p = new URLSearchParams({ option_id: uploadOptionId, ticket_type: uploadType, date: uploadDate, time_slot: uploadTimeSlot || '' });
            const res = await axios.post(`${API}/api/admin/listings/${listingId}/tickets?${p}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(`${res.data.uploaded} ticket(s) uploaded`); fetchTickets();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setUploading(false); e.target.value = ''; }
    };

    const handleDelete = async (ticketId) => {
        if (!window.confirm('Delete this ticket?')) return;
        try { await axios.delete(`${API}/api/admin/listings/tickets/${ticketId}`, { withCredentials: true }); toast.success('Deleted'); fetchTickets(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const ticketOptions = (listing?.options || []).filter(o => o.has_tickets);
    const allTimeSlotsFlat = Object.values(listing?.schedule || {}).flat().map(s => s.time).filter((v, i, a) => a.indexOf(v) === i).sort();
    const optionNameById = Object.fromEntries((listing?.options || []).map(o => [o.id, o.name]));
    const typeColors = { adult: 'bg-blue-500/10 text-blue-400', child: 'bg-green-500/10 text-green-400', infant: 'bg-purple-500/10 text-purple-400' };

    return (
        <DashboardLayout type="admin">
            <div className="space-y-6">
                <div>
                    <Link to="/admin/listings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Listings</Link>
                    <h1 className="text-2xl font-outfit font-bold text-white" data-testid="tickets-page-title">Ticket Inventory {listing && <span className="text-gold">— {listing.title}</span>}</h1>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-[#121620] border-white/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-white">{summary.total}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
                    <Card className="bg-[#121620] border-white/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-400">{summary.available}</p><p className="text-xs text-muted-foreground">Available</p></CardContent></Card>
                    <Card className="bg-[#121620] border-white/10"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-gold">{summary.assigned}</p><p className="text-xs text-muted-foreground">Assigned</p></CardContent></Card>
                </div>

                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-6">
                        <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Upload className="w-4 h-4 text-gold" /> Upload Tickets</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
                            <div>
                                <Label className="text-white/70 text-xs">Product Option</Label>
                                <Select value={uploadOptionId} onValueChange={setUploadOptionId}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select option" /></SelectTrigger>
                                    <SelectContent>{ticketOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/70 text-xs">Type</Label>
                                <Select value={uploadType} onValueChange={setUploadType}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="adult">Adult</SelectItem><SelectItem value="child">Child</SelectItem><SelectItem value="infant">Infant</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/70 text-xs">Date</Label>
                                <Input type="date" value={uploadDate} onChange={e => setUploadDate(e.target.value)} className="bg-white/5 border-white/10 text-white" />
                            </div>
                            <div>
                                <Label className="text-white/70 text-xs">Time Slot</Label>
                                <Select value={uploadTimeSlot} onValueChange={setUploadTimeSlot}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>{allTimeSlotsFlat.map(ts => <SelectItem key={ts} value={ts}>{ts}</SelectItem>)}<SelectItem value="any">Any time</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="cursor-pointer"><input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
                                    <Button type="button" className="bg-gold text-[#0A0D14] hover:bg-gold/90 w-full gap-2" disabled={uploading} asChild><span>{uploading ? 'Uploading...' : 'Upload PDFs'} <Upload className="w-4 h-4" /></span></Button>
                                </label>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Each file = 1 ticket in stock.</p>
                    </CardContent>
                </Card>

                <div className="flex flex-wrap items-center gap-3">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    {ticketOptions.length > 0 && (
                        <Select value={filterOption} onValueChange={v => setFilterOption(v === 'all' ? '' : v)}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white w-44"><SelectValue placeholder="All options" /></SelectTrigger>
                            <SelectContent><SelectItem value="all">All options</SelectItem>{ticketOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                    <Select value={filterType} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white w-28"><SelectValue placeholder="All types" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="adult">Adult</SelectItem><SelectItem value="child">Child</SelectItem><SelectItem value="infant">Infant</SelectItem></SelectContent>
                    </Select>
                    <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="bg-white/5 border-white/10 text-white w-44" />
                    {(filterOption || filterType || filterDate) && <Button variant="ghost" size="sm" onClick={() => { setFilterOption(''); setFilterType(''); setFilterDate(''); }} className="text-muted-foreground">Clear</Button>}
                </div>

                {loading ? (
                    <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
                ) : tickets.length === 0 ? (
                    <Card className="bg-[#121620] border-white/10"><CardContent className="py-12 text-center"><FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No tickets uploaded yet.</p></CardContent></Card>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow className="border-white/10"><TableHead>File</TableHead><TableHead>Option</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                            <TableBody>{tickets.map(t => (
                                <TableRow key={t.id} className="border-white/10">
                                    <TableCell><div className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" /><span className="text-white text-sm truncate max-w-[160px]">{t.file_name}</span></div></TableCell>
                                    <TableCell className="text-muted-foreground text-xs">{optionNameById[t.option_id] || '—'}</TableCell>
                                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${typeColors[t.ticket_type] || ''}`}>{t.ticket_type}</span></TableCell>
                                    <TableCell className="text-muted-foreground text-sm">{t.date || '—'}</TableCell>
                                    <TableCell className="text-muted-foreground text-sm">{t.time_slot || '—'}</TableCell>
                                    <TableCell><span className={`text-xs px-2 py-1 rounded-full ${t.status === 'available' ? 'bg-green-500/10 text-green-400' : 'bg-gold/10 text-gold'}`}>{t.status}</span></TableCell>
                                    <TableCell className="text-right">{t.status === 'available' && <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
